import { findBestMatches } from "./columnMatcher";

// Common variations of column names for each field
const columnMappings = {
  Date: [
    "date",
    "timestamp",
    "time",
    "datetime",
    "transaction_date",
    "tx_date",
    "created",
    "created_at",
    "executed_at",
    "trade_date",
  ],
  "Asset Sent": [
    "sent",
    "from_asset",
    "source",
    "sold",
    "asset_sold",
    "asset_out",
    "sent_asset",
    "from_currency",
    "sell_currency",
    "disposed_asset",
    "source_asset",
    "outgoing_asset",
    "base_currency",
  ],
  "Quantity Sent": [
    "amount_sent",
    "sent_amount",
    "from_amount",
    "sold_amount",
    "amount_out",
    "sent_quantity",
    "quantity_out",
    "sell_amount",
    "disposed_amount",
    "outgoing_amount",
    "base_amount",
    "source_amount",
  ],
  "Asset Received": [
    "received",
    "to_asset",
    "destination",
    "bought",
    "asset_bought",
    "asset_in",
    "received_asset",
    "to_currency",
    "buy_currency",
    "acquired_asset",
    "target_asset",
    "incoming_asset",
    "quote_currency",
  ],
  "Quantity Received": [
    "amount_received",
    "received_amount",
    "to_amount",
    "bought_amount",
    "amount_in",
    "received_quantity",
    "quantity_in",
    "buy_amount",
    "acquired_amount",
    "incoming_amount",
    "quote_amount",
    "target_amount",
  ],
  "USD Value": [
    "usd",
    "usd_value",
    "fiat_value",
    "value_usd",
    "dollar_value",
    "price_usd",
    "total_usd",
    "usd_equivalent",
    "fiat_equivalent",
    "total_value",
    "value",
    "spot_price_usd",
    "spot_rate",
    "market_value",
  ],
  "Fee Asset": [
    "fee_currency",
    "fee_asset",
    "fee_token",
    "gas_asset",
    "fee_coin",
    "network_fee_currency",
    "transaction_fee_currency",
    "gas_token",
    "mining_fee_currency",
  ],
  "Fee Quantity": [
    "fee_amount",
    "fee",
    "gas_amount",
    "fee_quantity",
    "gas_fee",
    "transaction_fee",
    "network_fee",
    "mining_fee",
    "gas_cost",
    "gas_used",
    "fee_value",
  ],
  "Fee USD Value": [
    "fee_usd",
    "fee_value_usd",
    "gas_fee_usd",
    "fee_fiat",
    "fee_dollar_value",
    "network_fee_usd",
    "transaction_fee_usd",
    "gas_cost_usd",
    "mining_fee_usd",
  ],
  Transaction: [
    "type",
    "transaction_type",
    "tx_type",
    "category",
    "operation",
    "action",
    "trade_type",
    "event_type",
    "transfer_type",
    "transaction_category",
    "side",
  ],
};

// Define required fields
const requiredFields = Object.keys(columnMappings);

// Helper function to generate variants of a string
const generateVariants = (str) => {
  const base = str.toLowerCase();
  const variants = new Set([base]);

  // Remove all spaces/underscores/dashes
  variants.add(base.replace(/[-_\s]/g, ""));

  // Replace spaces/underscores/dashes with each other
  variants.add(base.replace(/[-\s]/g, "_")); // with_underscores
  variants.add(base.replace(/[_\s]/g, "-")); // with-dashes
  variants.add(base.replace(/[-_]/g, " ")); // with spaces

  // Camel case variant
  const camelCase = base
    .replace(/[-_\s](.)/g, (_, c) => c.toUpperCase())
    .replace(/^(.)/, (c) => c.toLowerCase());
  variants.add(camelCase);

  // Pascal case variant
  const pascalCase = base
    .replace(/[-_\s](.)/g, (_, c) => c.toUpperCase())
    .replace(/^(.)/, (c) => c.toUpperCase());
  variants.add(pascalCase);

  return Array.from(variants);
};

// Helper function to clean CSV headers
const cleanHeader = (header) => {
  return header
    .trim()
    .replace(/^["']|["']$/g, "") // Remove leading/trailing quotes
    .toLowerCase()
    .trim();
};

// Expand column mappings with variants
const expandedColumnMappings = Object.entries(columnMappings).reduce(
  (acc, [key, values]) => {
    const expandedValues = new Set();
    values.forEach((value) => {
      generateVariants(value).forEach((variant) => expandedValues.add(variant));
    });
    acc[key] = Array.from(expandedValues);
    return acc;
  },
  {}
);

export const suggestColumnMappings = (headers) => {
  const suggestions = {};
  let remainingHeaders = [...headers];

  // Step 1: Exact matches (case-insensitive match with the required field name only)
  headers.forEach((header) => {
    const cleanedHeader = cleanHeader(header);
    for (const field of requiredFields) {
      // Skip if this field is already matched
      if (suggestions[field]) continue;

      // Check for exact match with the field name itself
      if (cleanHeader(field) === cleanedHeader) {
        suggestions[field] = { header, matchType: "exact" };
        remainingHeaders = remainingHeaders.filter((h) => h !== header);
        break;
      }
    }
  });

  // Step 2: Variant matches (matching against all variations)
  remainingHeaders = [...remainingHeaders];
  remainingHeaders.forEach((header) => {
    const cleanedHeader = cleanHeader(header);

    for (const [field, variations] of Object.entries(columnMappings)) {
      // Skip if this field is already matched
      if (suggestions[field]) continue;

      // Check if the cleaned header matches any variation
      if (variations.some((v) => cleanHeader(v) === cleanedHeader)) {
        suggestions[field] = { header, matchType: "variant" };
        remainingHeaders = remainingHeaders.filter((h) => h !== header);
        break;
      }
    }
  });

  // Step 3: More complex variant matches using generateVariants
  remainingHeaders = [...remainingHeaders];
  remainingHeaders.forEach((header) => {
    const headerVariants = generateVariants(cleanHeader(header));

    for (const [field, variations] of Object.entries(columnMappings)) {
      // Skip if this field is already matched
      if (suggestions[field]) continue;

      // Generate variants for each variation
      const allVariants = variations.flatMap((v) => generateVariants(v));

      // Check if any header variant matches any field variant
      const hasVariantMatch = headerVariants.some((headerVar) =>
        allVariants.some((variation) => variation === headerVar)
      );

      if (hasVariantMatch) {
        suggestions[field] = { header, matchType: "variant" };
        remainingHeaders = remainingHeaders.filter((h) => h !== header);
        break;
      }
    }
  });

  // Step 4: Similarity scoring for remaining fields
  if (remainingHeaders.length > 0) {
    const unmatchedFields = requiredFields.filter(
      (field) => !suggestions[field]
    );
    const { suggestions: similarityMatches } = findBestMatches(
      remainingHeaders,
      unmatchedFields,
      expandedColumnMappings
    );

    // Add similarity matches for fields that haven't been matched yet
    Object.entries(similarityMatches).forEach(([field, header]) => {
      if (!suggestions[field]) {
        suggestions[field] = { header, matchType: "similarity" };
        remainingHeaders = remainingHeaders.filter((h) => h !== header);
      }
    });
  }

  return {
    suggestions,
    unmappedColumns: remainingHeaders,
    warnings: [],
  };
};
